import { useEffect, useState } from 'react';

import {
  type ConsentState,
  useCookieConsent,
} from 'contexts/CookieConsentContext';

import { ShieldIcon } from '@polyai/ui/components/atoms/Icons';
import Text from '@polyai/ui/components/atoms/Text';
import Toggle from '@polyai/ui/components/atoms/Toggle';
import { Modal } from '@polyai/ui/components/molecules/Modal';

interface CookieSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CATEGORY_DESCRIPTIONS: Record<
  keyof Omit<ConsentState, 'necessary'>,
  string
> = {
  preferences:
    'Allow cookies that remember your preferences and personalize your experience.',
  statistics:
    'Allow cookies that help us understand how you use our website to improve it.',
  marketing:
    'Allow cookies that help us show you relevant ads and measure their effectiveness.',
};

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { consent, submitConsent, isConsentReady } = useCookieConsent();

  const [localConsent, setLocalConsent] = useState<
    Omit<ConsentState, 'necessary'>
  >({
    preferences: consent.preferences,
    statistics: consent.statistics,
    marketing: consent.marketing,
  });

  useEffect(() => {
    if (open && isConsentReady) {
      setLocalConsent({
        preferences: consent.preferences,
        statistics: consent.statistics,
        marketing: consent.marketing,
      });
    }
  }, [open, isConsentReady, consent]);

  const handleToggle = (category: keyof Omit<ConsentState, 'necessary'>) => {
    setLocalConsent(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    if (!isConsentReady) {
      return;
    }
    submitConsent(localConsent);
    onOpenChange?.(false);
  };

  const handleAcceptAll = () => {
    if (!isConsentReady) {
      return;
    }
    submitConsent({
      preferences: true,
      statistics: true,
      marketing: true,
    });
    onOpenChange?.(false);
  };

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange}>
      <Modal.Content size="md">
        <Modal.Title>Cookie settings</Modal.Title>

        <Text className="mt-sm2" color="textSecondary">
          Manage your cookie preferences. Necessary cookies are always enabled
          as they are essential for the website to function.
        </Text>

        <div className="mt-sm4 min-h-[560px]">
          <div className="flex items-center justify-between py-sm3 border-b border-border-subtle">
            <div className="flex flex-col gap-xs1">
              <div className="flex gap-sm2 items-center">
                <ShieldIcon size="sm" />
                <Text weight="semibold">Necessary</Text>
              </div>
              <Text color="textSecondary" size="body.small">
                Essential cookies required for the website to function properly.
              </Text>
            </div>
            <Toggle toggleOn disabled />
          </div>

          <div className="flex items-center justify-between py-sm3 border-b border-border-subtle">
            <div className="flex flex-col gap-xs1">
              <Text weight="semibold">Preferences</Text>
              <Text color="textSecondary" size="body.small">
                {CATEGORY_DESCRIPTIONS.preferences}
              </Text>
            </div>
            <Toggle
              toggleOn={localConsent.preferences}
              handleToggle={() => handleToggle('preferences')}
            />
          </div>

          <div className="flex items-center justify-between py-sm3 border-b border-border-subtle">
            <div className="flex flex-col gap-xs1">
              <Text weight="semibold">Statistics</Text>
              <Text color="textSecondary" size="body.small">
                {CATEGORY_DESCRIPTIONS.statistics}
              </Text>
            </div>
            <Toggle
              toggleOn={localConsent.statistics}
              handleToggle={() => handleToggle('statistics')}
            />
          </div>

          <div className="flex items-center justify-between py-sm3">
            <div className="flex flex-col gap-xs1">
              <Text weight="semibold">Marketing</Text>
              <Text color="textSecondary" size="body.small">
                {CATEGORY_DESCRIPTIONS.marketing}
              </Text>
            </div>
            <Toggle
              toggleOn={localConsent.marketing}
              handleToggle={() => handleToggle('marketing')}
            />
          </div>
        </div>

        <Modal.Footer
          primary={{
            label: 'Accept all',
            onClick: handleAcceptAll,
          }}
          secondary={{
            label: 'Save preferences',
            onClick: handleSave,
            variant: 'GHOST',
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};

export default CookieSettingsModal;
